/** admin-svc config routes — dossier template CRUD, doc-type registry, tenants. */

import type {
  DocType,
  DossierTemplate,
  IsoDateTime,
  TemplateCreateRequest,
  TemplateFlowType,
  TemplateItemInput,
  TemplateUpdateRequest,
  Uuid,
} from "@re-platform/types";

import type { ApiClient } from "../client";
import type { BillingPlanBody, BillingPlanOut } from "./billing";

export type TenantStatus = "PENDING" | "ACTIVE" | "SUSPENDED" | "CANCELLED";

/** TenantOut — mirrors admin-svc src/schemas/tenants.py. */
export type BrokerageType = "REAL_ESTATE" | "MORTGAGE";

export interface Tenant {
  id: Uuid;
  slug: string;
  display_name: string;
  status: TenantStatus;
  brokerage_type: BrokerageType;
  /** NULL until an Auth0 Organization is linked (always NULL in local mode). */
  auth0_org_id: string | null;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
}

/** TenantWithPlanOut — list/create view, tenant plus its billing plan. */
export interface TenantWithPlan extends Tenant {
  billing_plan: BillingPlanOut | null;
  /** From the brokerage profile, so both are null until it is filled in. */
  support_email: string | null;
  primary_phone: string | null;
}

/** TenantCreate — POST body for provisioning a new brokerage. */
export interface TenantCreateRequest {
  slug: string;
  display_name: string;
  brokerage_type?: BrokerageType;
  billing_plan?: BillingPlanBody | null;
}

/** TenantDomainOut — mirrors admin-svc src/schemas/tenants.py. */
export interface TenantDomain {
  id: Uuid;
  tenant_id: Uuid;
  domain: string;
  rp_id: string;
  is_primary: boolean;
  verified_at: IsoDateTime | null;
  created_at: IsoDateTime;
}

/** TenantDomainCreate — POST body for registering a custom domain. */
export interface TenantDomainCreateRequest {
  domain: string;
  /** Auto-computed from last two labels if omitted. */
  rp_id?: string | null;
}

export const adminService = {
  listTemplates(
    client: ApiClient,
    params?: { flow_type?: TemplateFlowType; include_inactive?: boolean },
  ) {
    return client.request<DossierTemplate[]>(
      "/admin/config/dossier-templates",
      {
        query: params,
      },
    );
  },

  getTemplate(client: ApiClient, templateId: Uuid) {
    return client.request<DossierTemplate>(
      `/admin/config/dossier-templates/${templateId}`,
    );
  },

  createTemplate(client: ApiClient, body: TemplateCreateRequest) {
    return client.request<DossierTemplate>("/admin/config/dossier-templates", {
      method: "POST",
      body,
    });
  },

  /** Fork a platform default into a tenant-scoped copy (same template_key). */
  forkTemplate(client: ApiClient, templateId: Uuid) {
    return client.request<DossierTemplate>(
      `/admin/config/dossier-templates/${templateId}/fork`,
      { method: "POST" },
    );
  },

  updateTemplate(
    client: ApiClient,
    templateId: Uuid,
    body: TemplateUpdateRequest,
  ) {
    return client.request<DossierTemplate>(
      `/admin/config/dossier-templates/${templateId}`,
      {
        method: "PATCH",
        body,
      },
    );
  },

  /** Replace the slot list (diffed server-side; in-use removals 409). */
  replaceTemplateItems(
    client: ApiClient,
    templateId: Uuid,
    items: readonly TemplateItemInput[],
  ) {
    return client.request<DossierTemplate>(
      `/admin/config/dossier-templates/${templateId}/items`,
      { method: "PUT", body: { items } },
    );
  },

  deleteTemplate(client: ApiClient, templateId: Uuid) {
    return client.request<void>(
      `/admin/config/dossier-templates/${templateId}`,
      {
        method: "DELETE",
      },
    );
  },

  listDocTypes(client: ApiClient, params?: { active?: boolean }) {
    return client.request<DocType[]>("/admin/config/doc-types", {
      query: params,
    });
  },

  /** List tenants (with their billing plan join). super_admin only. */
  listTenants(client: ApiClient, params?: { status?: TenantStatus }) {
    return client.request<TenantWithPlan[]>("/admin/tenants", {
      query: params,
    });
  },

  /** Provision a new brokerage tenant (PENDING; mirrors to an Auth0 Org). */
  createTenant(client: ApiClient, body: TenantCreateRequest) {
    return client.request<TenantWithPlan>("/admin/tenants", {
      method: "POST",
      body,
    });
  },

  activateTenant(client: ApiClient, tenantId: Uuid) {
    return client.request<Tenant>(`/admin/tenants/${tenantId}/activate`, {
      method: "POST",
    });
  },

  deactivateTenant(client: ApiClient, tenantId: Uuid) {
    return client.request<Tenant>(`/admin/tenants/${tenantId}/deactivate`, {
      method: "POST",
    });
  },

  listTenantDomains(client: ApiClient, tenantId: Uuid) {
    return client.request<TenantDomain[]>(`/admin/tenants/${tenantId}/domains`);
  },

  addTenantDomain(
    client: ApiClient,
    tenantId: Uuid,
    body: TenantDomainCreateRequest,
  ) {
    return client.request<TenantDomain>(`/admin/tenants/${tenantId}/domains`, {
      method: "POST",
      body,
    });
  },

  deleteTenantDomain(client: ApiClient, tenantId: Uuid, domainId: Uuid) {
    return client.request<void>(
      `/admin/tenants/${tenantId}/domains/${domainId}`,
      { method: "DELETE" },
    );
  },

  verifyTenantDomain(client: ApiClient, tenantId: Uuid, domainId: Uuid) {
    return client.request<TenantDomain>(
      `/admin/tenants/${tenantId}/domains/${domainId}/verify`,
      { method: "POST" },
    );
  },
};

// -- Staff invitations + onboarding (Workstream B) ---------------------------

export type StaffRole = "brokerage_admin" | "agent";
export type StaffInvitationStatus =
  "PENDING" | "ACCEPTED" | "REVOKED" | "EXPIRED";

/** StaffInvitationOut — mirrors admin-svc src/schemas/staff.py. */
export interface StaffInvitation {
  id: Uuid;
  tenant_id: Uuid;
  email: string;
  primary_role: StaffRole;
  status: StaffInvitationStatus;
  invited_by_user_id: Uuid;
  expires_at: IsoDateTime;
  accepted_user_id: Uuid | null;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
}

/** Create result — adds the one-time accept link (raw token, issuer-only). */
export interface StaffInvitationCreated extends StaffInvitation {
  accept_url: string;
}

export interface StaffInvitationCreateRequest {
  email: string;
  primary_role: StaffRole;
  /** Required for super_admin; ignored (own tenant) for brokerage_admin. */
  tenant_id?: Uuid;
}

/** StaffMemberOut — a tenant's staff user + agent-profile fields. */
export interface StaffMember {
  id: Uuid;
  email: string;
  full_name: string | null;
  /** E.164 cross-tenant agent key (V043); null until captured. */
  phone_e164: string | null;
  primary_role: string;
  status: string;
  last_login_at: IsoDateTime | null;
  created_at: IsoDateTime;
  agent_id: Uuid | null;
  trec_license_number: string | null;
  nmls_id: string | null;
  /** Count of normalised agent_licenses rows held by this staff member. */
  license_count: number;
}

/** What the public accept page renders itself from. */
export interface StaffInvitationLookup {
  email: string;
  primary_role: string;
  tenant_display_name: string;
  brokerage_type: "REAL_ESTATE" | "MORTGAGE";
}

/** StaffAcceptRequest — public accept route on identity-svc (no session). */
export interface StaffAcceptRequest {
  token: string;
  full_name?: string;
  phone?: string;
  trec_license_number?: string;
  /** A mortgage brokerage's agents hold this instead of a TREC licence. */
  nmls_id?: string;
}

export interface StaffAcceptResponse {
  status: string;
  user_id: Uuid;
  tenant_id: Uuid;
  primary_role: string;
  email: string;
  /** Auth0 password-change ticket URL (null in local mode / Auth0 degraded). */
  credential_set_url: string | null;
}

export const staffService = {
  /** List invitations (super_admin any tenant; brokerage_admin own). */
  listInvitations(
    client: ApiClient,
    params?: { tenant_id?: Uuid; status?: StaffInvitationStatus },
  ) {
    return client.request<StaffInvitation[]>("/admin/staff/invitations", {
      query: params,
    });
  },

  /** Invite a brokerage_admin or agent. Returns the one-time accept link. */
  createInvitation(client: ApiClient, body: StaffInvitationCreateRequest) {
    return client.request<StaffInvitationCreated>("/admin/staff/invitations", {
      method: "POST",
      body,
    });
  },

  revokeInvitation(client: ApiClient, invitationId: Uuid) {
    return client.request<StaffInvitation>(
      `/admin/staff/invitations/${invitationId}/revoke`,
      { method: "POST" },
    );
  },

  /**
   * Re-send a PENDING invitation. The token is ROTATED, so any earlier link
   * for this invitation stops working — a staff invite is a credential, and
   * only the newest email should be redeemable. Returns the fresh accept_url.
   */
  resendInvitation(client: ApiClient, invitationId: Uuid) {
    return client.request<StaffInvitationCreated>(
      `/admin/staff/invitations/${invitationId}/resend`,
      { method: "POST" },
    );
  },

  /** List a tenant's staff users + agent profiles. */
  listStaff(client: ApiClient, params?: { tenant_id?: Uuid }) {
    return client.request<StaffMember[]>("/admin/staff", { query: params });
  },

  /** Public — accept an invitation (identity-svc; no session). */
  /** What the PUBLIC accept page may know before anyone signs in — enough to
   *  greet the invitee and ask for the right licence number, nothing more. */
  lookupInvitation(client: ApiClient, token: string) {
    return client.request<StaffInvitationLookup>(
      "/identity/staff/invitations/lookup",
      { method: "POST", body: { token } },
    );
  },

  acceptInvitation(client: ApiClient, body: StaffAcceptRequest) {
    return client.request<StaffAcceptResponse>("/identity/staff/accept", {
      method: "POST",
      body,
    });
  },

  /** Suspend or reactivate a staff member — blocks their next auth check
   * immediately (both the passkey and Auth0-staff paths re-verify status
   * on every request). */
  updateStatus(
    client: ApiClient,
    userId: Uuid,
    status: "ACTIVE" | "SUSPENDED",
  ) {
    return client.request<StaffMember>(`/admin/staff/${userId}/status`, {
      method: "PATCH",
      body: { status },
    });
  },
};

// -- Agent licenses + phone (Phase 10 step 16) -------------------------------

export type LicenseType = "NMLS" | "TREC" | "DRE" | "BRE";

/** AgentLicenseOut — mirrors admin-svc src/schemas/licenses.py. */
export interface AgentLicense {
  id: Uuid;
  user_id: Uuid;
  tenant_id: Uuid;
  license_type: LicenseType;
  license_number: string;
  state: string | null;
  expires_at: string | null; // ISO date (YYYY-MM-DD)
  verified_at: IsoDateTime | null;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
}

export interface AgentLicenseCreateRequest {
  license_type: LicenseType;
  license_number: string;
  state?: string | null;
  expires_at?: string | null;
}

export interface AgentLicenseUpdateRequest {
  license_number?: string;
  state?: string | null;
  expires_at?: string | null;
  /** true stamps verified_at=now; false clears it; omit to leave unchanged. */
  verified?: boolean;
}

export interface AgentPhone {
  user_id: Uuid;
  phone_e164: string | null;
}

/** Agent license CRUD + phone capture. All keyed by the staff member's users(id). */
export const licenseService = {
  listLicenses(client: ApiClient, userId: Uuid) {
    return client.request<AgentLicense[]>(`/admin/agents/${userId}/licenses`);
  },

  createLicense(
    client: ApiClient,
    userId: Uuid,
    body: AgentLicenseCreateRequest,
  ) {
    return client.request<AgentLicense>(`/admin/agents/${userId}/licenses`, {
      method: "POST",
      body,
    });
  },

  updateLicense(
    client: ApiClient,
    userId: Uuid,
    licenseId: Uuid,
    body: AgentLicenseUpdateRequest,
  ) {
    return client.request<AgentLicense>(
      `/admin/agents/${userId}/licenses/${licenseId}`,
      { method: "PATCH", body },
    );
  },

  deleteLicense(client: ApiClient, userId: Uuid, licenseId: Uuid) {
    return client.request<void>(
      `/admin/agents/${userId}/licenses/${licenseId}`,
      {
        method: "DELETE",
      },
    );
  },

  /** Set the agent's phone (free-text in, normalized to E.164 server-side). */
  setPhone(client: ApiClient, userId: Uuid, phone: string) {
    return client.request<AgentPhone>(`/admin/agents/${userId}/phone`, {
      method: "PATCH",
      body: { phone },
    });
  },
};

// -- Brokerage memberships (identity-svc; Phase 10 step 7/16) -----------------

/** TenantMembership — mirrors identity-svc src/schemas/memberships.py. */
export interface TenantMembership {
  tenant_id: Uuid;
  slug: string;
  display_name: string;
  brokerage_type: string;
  role: string;
  /** Auth0 Organization id (org_…) for loginWithRedirect; null if none. */
  auth0_org_id: string | null;
}

export const membershipService = {
  /** The signed-in staff member's brokerages (for the brokerage switcher). */
  listMemberships(client: ApiClient) {
    return client.request<{ memberships: TenantMembership[] }>(
      "/identity/memberships",
    );
  },
};

export interface ClientInviteCreated {
  token: string;
  expires_at: string;
  invite_url: string;
  /** True when identity-svc queued a client.invited email for delivery. */
  email_queued: boolean;
}

/**
 * A row from GET /identity/client-invites — the agent's own open AND expired
 * invites (expired ones flagged). Used and revoked invites are never returned.
 */
export interface ClientInvitePending {
  token: string;
  invite_url: string;
  /** null when the agent minted a link to share themselves (no email on file). */
  client_email: string | null;
  client_name: string | null;
  created_at: string;
  expires_at: string;
  /** Past expiry — listed in the collapsed Expired section; Resend re-opens it. */
  expired: boolean;
  resent_count: number;
  last_sent_at: string | null;
}

/** POST /identity/client-invites/{token}/revoke — the link stops working (spec 023). */
export interface ClientInviteRevoked {
  token: string;
  /** When it was withdrawn; a second revoke returns the same time. */
  revoked_at: string;
}

export interface ClientInviteResent {
  token: string;
  invite_url: string;
  expires_at: string;
  resent_count: number;
  email_queued: boolean;
}

export const clientInviteService = {
  /**
   * Mint a client invite link (agent JWT required). When `client_email` is
   * given the link is also emailed to the client via notification-svc.
   */
  create(
    client: ApiClient,
    body: { client_email?: string; client_name?: string },
  ) {
    return client.request<ClientInviteCreated>("/identity/client-invites", {
      method: "POST",
      body,
    });
  },

  /**
   * The calling agent's own invites, agent-scoped server-side: open + expired
   * (flagged); used and revoked are never returned.
   */
  listPending(client: ApiClient) {
    return client.request<ClientInvitePending[]>("/identity/client-invites");
  },

  /**
   * Re-send an invite email. The token is deliberately NOT rotated — the client
   * may still be holding the original email, and invalidating it would cause
   * the very failure this fixes. Expiry is extended — this is also how an
   * EXPIRED invite is re-opened. 409s when the invite was already accepted, was
   * revoked, or has no email address on file.
   */
  resend(client: ApiClient, token: string) {
    return client.request<ClientInviteResent>(
      `/identity/client-invites/${encodeURIComponent(token)}/resend`,
      { method: "POST" },
    );
  },

  /**
   * Withdraw an invite: its link stops working. Accounts already created from it
   * keep their records. Own invites only (404 otherwise); idempotent. Spec 023.
   */
  revoke(client: ApiClient, token: string) {
    return client.request<ClientInviteRevoked>(
      `/identity/client-invites/${encodeURIComponent(token)}/revoke`,
      { method: "POST" },
    );
  },
};
